import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Tag, Truck, Store } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, RON, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import GoogleButton from "@/components/GoogleButton";
import { toast } from "sonner";

const PAYMENTS = [
  { v: "card", l: "Card online" },
  { v: "ramburs", l: "Ramburs la livrare" },
  { v: "transfer", l: "Transfer bancar" },
];

export default function Checkout() {
  const { items, total: subtotal, clear } = useCart();
  const { user, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [placed, setPlaced] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [claimPwd, setClaimPwd] = useState("");
  const [claiming, setClaiming] = useState(false);

  const claimAccount = async (e) => {
    e.preventDefault();
    setClaiming(true);
    try {
      const { data } = await api.post("/auth/claim-account", {
        email: placed.email || form.email,
        password: claimPwd,
        name: placed.full_name || form.full_name,
      });
      loginWithToken(data.token, data.user);
      toast.success("Cont creat cu succes!");
      navigate("/cont");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  };
  const [ship, setShip] = useState({ threshold: 249, courier_cost: 24.99, cod_fee: 6.99, pickup_address: "" });
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null); // {code, discount}
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    street: "", street_no: "", building: "", apartment: "", floor: "", postal_code: "",
    address: "", city: "", county: "",
    payment_method: "card", delivery_method: "curier",
    notes: "", is_company: false, company_name: "", cui: "", reg_com: "", terms_accepted: false,
  });
  const isPickup = form.delivery_method === "ridicare";
  const isCurier = form.delivery_method === "curier";
  const isRamburs = form.payment_method === "ramburs";

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/shipping/info").then(({ data }) => setShip(data));
    if (user) {
      setForm((f) => ({
        ...f, full_name: user.name || f.full_name, email: user.email || f.email,
        phone: user.phone || f.phone, address: user.address || f.address,
        city: user.city || f.city, county: user.county || f.county,
      }));
    }
  }, [user]);

  const discount = applied?.discount || 0;
  const afterDiscount = Math.max(0, subtotal - discount);
  const freeShip = afterDiscount >= ship.threshold || afterDiscount === 0;
  const shipping = isPickup ? 0 : (freeShip ? 0 : ship.courier_cost);
  const codFee = (isRamburs && !isPickup) ? ship.cod_fee : 0;
  const grandTotal = afterDiscount + shipping + codFee;

  if (placed) {
    return (
      <div className="pt-40 pb-40 max-w-xl mx-auto text-center px-5">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="h-20 w-20 mx-auto grid place-items-center rounded-full bg-roast text-cream"><Check size={36} /></motion.div>
        <h1 className="font-display text-5xl tracking-tight mt-8" data-testid="order-success">Comandă plasată!</h1>
        <p className="text-muted-foreground mt-4">Comanda ta <span className="text-roast font-semibold">{placed.order_number}</span> a fost înregistrată. Vei primi un email de confirmare.</p>

        {!user && (
          <div className="mt-10 border border-line bg-surface p-6 text-left" data-testid="post-order-account">
            <p className="font-display text-2xl text-center">Creează-ți un cont</p>
            <p className="text-sm text-muted-foreground text-center mt-1">Salvează-ți comenzile și comandă mai rapid data viitoare.</p>
            <form onSubmit={claimAccount} className="mt-5 space-y-3">
              <input type="password" required minLength={6} value={claimPwd} onChange={(e) => setClaimPwd(e.target.value)}
                placeholder="Alege o parolă (min. 6 caractere)" data-testid="claim-password"
                className="w-full bg-white border border-line px-4 py-3 text-sm outline-none focus:border-roast transition-colors" />
              <button type="submit" disabled={claiming} data-testid="claim-submit"
                className="w-full bg-ink text-cream py-3.5 text-sm font-semibold hover:bg-roast transition-colors disabled:opacity-60">
                {claiming ? "Se creează..." : `Creează cont pentru ${placed.email || form.email}`}
              </button>
            </form>
            <div className="flex items-center gap-4 my-4">
              <span className="h-px bg-line flex-1" /><span className="text-xs text-muted-foreground uppercase">sau</span><span className="h-px bg-line flex-1" />
            </div>
            <GoogleButton next="/cont" label="Creează cont cu Google" testid="claim-google" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          {user && <Link to="/cont" className="bg-ink text-cream px-8 py-4 hover:bg-roast transition-colors text-sm font-semibold">Vezi comenzile</Link>}
          <Link to="/produse" className="border border-line px-8 py-4 hover:border-ink transition-colors text-sm font-semibold">Continuă cumpărăturile</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-40 text-center px-5">
        <h1 className="font-display text-5xl tracking-tight">Coșul este gol</h1>
        <Link to="/produse" className="inline-block mt-8 bg-ink text-cream px-8 py-4 hover:bg-roast transition-colors text-sm font-semibold">Descoperă cafeaua</Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setApplied({ code: data.code, discount: data.discount });
      toast.success(`Cod aplicat: -${RON(data.discount)}`);
    } catch (err) {
      setApplied(null);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) { toast.error("Trebuie să accepți termenii și condițiile"); return; }
    setSubmitting(true);
    const composedAddress = isCurier ? [
      form.street && `Str. ${form.street}`,
      form.street_no && `nr. ${form.street_no}`,
      form.building && `bl. ${form.building}`,
      form.apartment && `ap. ${form.apartment}`,
      form.floor && `et. ${form.floor}`,
      form.postal_code && `cod ${form.postal_code}`,
    ].filter(Boolean).join(", ") : "";
    const lean = items.map((i) => ({ product_id: i.product_id, grammage: i.grammage, quantity: i.quantity }));
    const payload = { ...form, address: composedAddress, items: lean, discount_code: applied?.code || "", origin_url: window.location.origin };
    if (isPickup) { payload.city = payload.city || "Ridicare"; payload.county = payload.county || "-"; }
    try {
      if (form.payment_method === "card") {
        const { data } = await api.post("/payments/checkout", { ...payload, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await api.post("/orders", payload);
      clear();
      setPlaced(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <Seo title="Finalizare comandă" description="Finalizează comanda la Coffee Cup Supply." />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
        <p className="overline text-roast">Finalizare</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-3 mb-12">Detalii comandă</h1>

        <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-16" data-testid="checkout-form">
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nume complet" value={form.full_name} onChange={set("full_name")} testid="checkout-name" />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="checkout-email" />
              <Field label="Telefon" value={form.phone} onChange={set("phone")} testid="checkout-phone" />
            </div>

            {/* delivery method */}
            <div>
              <label className="overline text-ink/50">Metodă de livrare</label>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <button type="button" onClick={() => setForm((f) => ({ ...f, delivery_method: "curier" }))}
                  data-testid="delivery-curier"
                  className={`flex items-center gap-3 px-4 py-4 text-sm border text-left transition-colors ${isCurier ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>
                  <Truck size={20} /><span><span className="font-semibold block">Livrare Cargus la adresă</span><span className="text-xs opacity-70">{RON(ship.courier_cost)} · 1–3 zile</span></span>
                </button>
                <button type="button" onClick={() => setForm((f) => ({ ...f, delivery_method: "ridicare" }))}
                  data-testid="delivery-ridicare"
                  className={`flex items-center gap-3 px-4 py-4 text-sm border text-left transition-colors ${isPickup ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>
                  <Store size={20} /><span><span className="font-semibold block">Ridicare de la sediu</span><span className="text-xs opacity-70">Gratuit</span></span>
                </button>
              </div>
            </div>

            {isPickup && (
              <div className="border border-line bg-surface p-5 text-sm" data-testid="pickup-address">
                <p className="overline text-ink/50 mb-2">Adresa de ridicare</p>
                <p className="text-ink leading-relaxed">{ship.pickup_address || "Coffee Cup Supply — te vom contacta cu detaliile de ridicare."}</p>
              </div>
            )}

            {isCurier && (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Stradă" value={form.street} onChange={set("street")} testid="checkout-street" required />
                  <Field label="Număr" value={form.street_no} onChange={set("street_no")} testid="checkout-street-no" required />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                  <Field label="Bloc/Casă" value={form.building} onChange={set("building")} testid="checkout-building" required={false} />
                  <Field label="Apartament" value={form.apartment} onChange={set("apartment")} testid="checkout-apartment" required={false} />
                  <Field label="Etaj" value={form.floor} onChange={set("floor")} testid="checkout-floor" required={false} />
                  <Field label="Cod poștal" value={form.postal_code} onChange={set("postal_code")} testid="checkout-postal" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Oraș/Localitate" value={form.city} onChange={set("city")} testid="checkout-city" required />
                  <Field label="Județ" value={form.county} onChange={set("county")} testid="checkout-county" required />
                </div>
              </>
            )}

            {/* company */}
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_company} onChange={(e) => setForm((f) => ({ ...f, is_company: e.target.checked }))} data-testid="checkout-is-company" className="accent-roast h-4 w-4" />
              Facturare pe firmă (persoană juridică)
            </label>
            {form.is_company && (
              <div className="grid sm:grid-cols-3 gap-5 border border-line p-5">
                <Field label="Denumire firmă" value={form.company_name} onChange={set("company_name")} testid="checkout-company" />
                <Field label="CUI" value={form.cui} onChange={set("cui")} testid="checkout-cui" />
                <Field label="Reg. Com." value={form.reg_com} onChange={set("reg_com")} testid="checkout-regcom" />
              </div>
            )}

            <div>
              <label className="overline text-ink/50">Metodă de plată</label>
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                {PAYMENTS.map((p) => (
                  <button key={p.v} type="button" onClick={() => setForm((f) => ({ ...f, payment_method: p.v }))}
                    data-testid={`payment-${p.v}`}
                    className={`px-4 py-3 text-sm border transition-colors ${form.payment_method === p.v ? "bg-ink text-cream border-ink" : "border-line hover:border-ink"}`}>{p.l}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="overline text-ink/50">Observații (opțional)</label>
              <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="checkout-notes" className="mt-2 w-full bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors resize-none" />
            </div>
          </div>

          {/* summary */}
          <div className="lg:sticky lg:top-28 h-fit bg-stone p-8">
            <h2 className="font-display text-3xl mb-6">Comanda ta</h2>
            <div className="space-y-4 max-h-56 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 text-sm">
                  {i.image ? <img src={i.image} alt={i.name} className="h-14 w-12 object-cover bg-surface" /> : <div className="h-14 w-12 bg-surface" />}
                  <div className="flex-1"><p className="font-medium leading-tight">{i.name}</p><p className="text-muted-foreground text-xs">{i.grammage} · {i.quantity} buc.</p></div>
                  <span>{RON(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>

            {/* coupon */}
            <div className="mt-6 pt-6 border-t border-line">
              <div className="flex gap-2">
                <div className="flex items-center gap-2 flex-1 border border-line bg-surface px-3">
                  <Tag size={15} className="text-roast" />
                  <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Cod reducere"
                    data-testid="coupon-input" className="flex-1 bg-transparent py-3 text-sm outline-none" />
                </div>
                <button type="button" onClick={applyCode} data-testid="coupon-apply" className="bg-ink text-cream px-5 text-sm font-semibold hover:bg-roast transition-colors">Aplică</button>
              </div>
              {applied && <p className="text-xs text-roast mt-2" data-testid="coupon-applied">Cod „{applied.code}" aplicat</p>}
            </div>

            <div className="space-y-3 text-sm mt-6">
              <Row label="Subtotal" value={RON(subtotal)} />
              {discount > 0 && <Row label={`Reducere`} value={`− ${RON(discount)}`} accent />}
              <Row label="Transport" value={shipping === 0 ? "Gratuit" : RON(shipping)} />
              {codFee > 0 && <Row label="Taxă ramburs" value={RON(codFee)} />}
              {isPickup && <p className="text-xs text-muted-foreground" data-testid="summary-pickup">Ridicare de la sediu · fără cost transport</p>}
              {!isPickup && shipping > 0 && <p className="text-xs text-muted-foreground">Transport gratuit peste {RON(ship.threshold)}</p>}
              {!isPickup && shipping === 0 && afterDiscount >= ship.threshold && <p className="text-xs text-roast" data-testid="summary-free-ship">Beneficiezi de transport gratuit</p>}
            </div>
            <div className="flex items-center justify-between border-t border-line mt-6 pt-6">
              <span className="overline">Total</span>
              <span className="font-display text-3xl" data-testid="checkout-total">{RON(grandTotal)}</span>
            </div>

            <label className="flex items-start gap-3 text-xs text-muted-foreground mt-6 cursor-pointer">
              <input type="checkbox" checked={form.terms_accepted} onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))} data-testid="checkout-terms" className="accent-roast h-4 w-4 mt-0.5" />
              <span>Sunt de acord cu <Link to="/termeni" className="text-roast underline">Termenii și condițiile</Link> și <Link to="/confidentialitate" className="text-roast underline">Politica de confidențialitate</Link>.</span>
            </label>

            <button type="submit" disabled={submitting} data-testid="place-order-btn"
              className="group mt-6 w-full flex items-center justify-center gap-3 bg-ink text-cream py-4 hover:bg-roast transition-colors text-sm font-semibold tracking-wide disabled:opacity-60">
              {submitting ? "Se procesează..." : "Plasează comanda"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={accent ? "text-roast font-medium" : "text-ink"}>{value}</span>
  </div>
);
const Field = ({ label, type = "text", value, onChange, testid, required = true }) => (
  <div>
    <label className="overline text-ink/50">{label}</label>
    <input required={required} type={type} value={value} onChange={onChange} data-testid={testid} className="mt-2 w-full bg-surface border border-line px-4 py-3 outline-none focus:border-roast transition-colors" />
  </div>
);
